import React from "react";

import { formatCmOutcome, getUserFacingApiErrorMessage, roundCmOutcome, useTranslation } from "utils";

import { CURRENCY_CODE, CurrencyCode } from "consts";

import { CalculateAnonymousForecastResponse } from "models";

import { Button, Icon, SoilSightLoader, Typography } from "components";

import { AcquisitionForecastChart } from "pages/app/soilsight-pre-login/components/AcquisitionForecastChart";
import { buildAnonymousForecastChartSeries, getAnonymousForecastCumulativeDelta } from "pages/app/soilsight-pre-login/utils/acquisitionForecast";

type ForecastStepProps = {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  forecast: CalculateAnonymousForecastResponse | null;
  onRetry: () => void;
  onCreateAccount: () => void;
};

export const ForecastStep = ({ isLoading, isError, error, forecast, onRetry, onCreateAccount }: ForecastStepProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  if (isLoading) {
    return (
      <div className="soilsight-acquisition-forecast soilsight-acquisition-forecast--state" data-testid="soilsight-step-forecast">
        <SoilSightLoader />
      </div>
    );
  }

  const chartData = forecast ? buildAnonymousForecastChartSeries(forecast) : [];

  // No projected years ... we treat it like a failed run, not an empty chart under a EUR 0 headline.
  if (isError || !forecast || !chartData.length) {
    return (
      <div className="soilsight-acquisition-forecast soilsight-acquisition-forecast--state" data-testid="soilsight-step-forecast">
        <Icon iconName="tabler/alert-circle" size="l" />
        <Typography variant="h4">{t("preLogin.simulation.forecast.errorTitle")}</Typography>
        <Typography variant="paragraph-m" color="grey-600">
          {getUserFacingApiErrorMessage(error, t("preLogin.simulation.forecast.errorBody"))}
        </Typography>
        <Button type="primary" dataTestId="soilsight-forecast-retry" onClick={onRetry}>
          {t("preLogin.simulation.forecast.retry")}
        </Button>
      </div>
    );
  }

  const currency = (forecast.currency || CURRENCY_CODE.EUR) as CurrencyCode;
  const gapFormat = { currency, currencyDisplay: "narrowSymbol" } as const;

  // We round before the sign test, so a shortfall that rounds away to zero keeps the neutral copy.
  const delta = roundCmOutcome(getAnonymousForecastCumulativeDelta(chartData));
  const copyVariant = delta < 0 ? "Negative" : "";

  // The prose states a magnitude. The callout keeps the sign, so a shortfall reads as one.
  const formattedGap = formatCmOutcome(Math.abs(delta), gapFormat);
  const gapLabel = delta === 0 ? null : formatCmOutcome(delta, gapFormat);

  return (
    <div className="soilsight-acquisition-forecast" data-testid="soilsight-step-forecast">
      <div className="soilsight-acquisition-forecast__intro">
        <Typography variant="h2" className="soilsight-acquisition-forecast__title" data-testid="soilsight-forecast-title">
          {t(`preLogin.simulation.forecast.headline${copyVariant}`, { amount: formattedGap })}
        </Typography>
        <Typography variant="paragraph-m" className="soilsight-acquisition-forecast__teaser" data-testid="soilsight-forecast-teaser">
          {t(`preLogin.simulation.forecast.teaser${copyVariant}`, { amount: formattedGap })}
        </Typography>
      </div>

      <Button type="primary" dataTestId="soilsight-forecast-create-account" className="soilsight-acquisition-forecast__cta" onClick={onCreateAccount}>
        {t("preLogin.simulation.cta.forecast")}
      </Button>

      <div className="soilsight-acquisition-forecast__chart-block">
        <div className="soilsight-acquisition-forecast__legend">
          <span className="soilsight-acquisition-forecast__legend-item">
            <span className="soilsight-acquisition-forecast__legend-dot soilsight-acquisition-forecast__legend-dot--baseline" />
            <Typography variant="paragraph-s">{t("preLogin.simulation.forecast.legendBaseline")}</Typography>
          </span>
          <span className="soilsight-acquisition-forecast__legend-item">
            <span className="soilsight-acquisition-forecast__legend-dot soilsight-acquisition-forecast__legend-dot--change" />
            <Typography variant="paragraph-s">{t("preLogin.simulation.forecast.legendChange")}</Typography>
          </span>
        </div>

        <AcquisitionForecastChart data={chartData} currency={currency} gapLabel={gapLabel} />
      </div>

      <Typography variant="paragraph-m" className="soilsight-acquisition-forecast__summary" data-testid="soilsight-forecast-summary">
        {t(`preLogin.simulation.forecast.summary${copyVariant}`, { amount: formattedGap, year: chartData.length })}
      </Typography>
    </div>
  );
};
